import { Component, ChangeDetectionStrategy, input, output, computed, signal, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LabelModule } from '@progress/kendo-angular-label';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { SwitchModule } from '@progress/kendo-angular-inputs';
import {
  securityCheckOutlineIcon,
  gearIcon,
  infoCircleIcon,
  exclamationCircleIcon,
  saveIcon,
  userIcon,
  clockIcon,
} from '@progress/kendo-svg-icons';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { MedrFormData } from 'src/app/core/models/customer';
import { MedrFormService } from './services/form.service';

@Component({
  selector: 'app-medr-service',
  standalone: true,
  templateUrl: './medr-service.component.html',
  styleUrls: ['./medr-service.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MedrFormService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputsModule,
    DropDownsModule,
    LabelModule,
    ButtonsModule,
    IconsModule,
    SwitchModule,
  ],
})
export class MedrServiceComponent extends BaseComponent {
  private formService = inject(MedrFormService);

  // Input/Output
  public initialData = input<MedrFormData | null>(null); // Optional initial data for editing
  public isNarrowLayout = input<boolean>(false);
  public dataUpdate = output<MedrFormData>();
  public validityUpdate = output<boolean>();

  // Kendo UI SVG Icons
  public shieldCheckIcon: SVGIcon = securityCheckOutlineIcon;
  public gearIcon: SVGIcon = gearIcon;
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public exclamationTriangleIcon: SVGIcon = exclamationCircleIcon;
  public serverIcon: SVGIcon = saveIcon;
  public shieldIcon: SVGIcon = securityCheckOutlineIcon;
  public networkIcon: SVGIcon = gearIcon;
  public userIcon: SVGIcon = userIcon;
  public clockIcon: SVGIcon = clockIcon;

  // Form
  public medrForm = this.formService.createMedrForm();

  // Service data
  public serviceOptions = this.formService.getServiceOptions();
  public slaOptions = this.formService.getSlaOptions();
  public deploymentOptions = this.formService.getDeploymentOptions();

  // Form validity signal
  public isFormValid = signal<boolean>(false);
  public selectedService = signal<string | null>(null);
  public showEdrConnectFields = computed(() => this.selectedService() === 'edr-connect');
  public showEdrCompleteFields = computed(() => this.selectedService() === 'edr-complete');
  public showEdrControlFields = computed(() => this.selectedService() === 'edr-control');
  public showEdrCmmcFields = computed(() => this.selectedService() === 'edr-cmmc');

  constructor() {
    super();
    this.initializeForm();
  }

  private initializeForm(): void {
    // Initialize with existing data if available
    const initialData = this.initialData();
    if (initialData) {
      // Recreate form with existing data
      this.medrForm = this.formService.createMedrForm(initialData);

      // Apply conditional validation if service is already selected
      if (initialData.selectedService) {
        this.selectedService.set(initialData.selectedService);
      }
    }

    // Watch for form changes and emit updates
    this.medrForm.valueChanges.subscribe((value) => {
      const formData = this.formService.getFormData(this.medrForm);
      this.dataUpdate.emit(formData);
      this.isFormValid.set(this.medrForm.valid);
      this.validityUpdate.emit(this.medrForm.valid);
    });

    // Watch for totalMachines changes specifically to ensure validation updates
    this.medrForm.get('totalMachines')?.valueChanges.subscribe(() => {
      this.medrForm.updateValueAndValidity();
      this.isFormValid.set(this.medrForm.valid);
      this.validityUpdate.emit(this.medrForm.valid);
    });

    // Emit initial validity state
    this.validityUpdate.emit(this.medrForm.valid);
  }

  public selectService(serviceValue: string): void {
    this.formService.selectService(this.medrForm, serviceValue);
    this.selectedService.set(serviceValue);
  }

  public getServiceDescription(serviceValue: string): string {
    return this.formService.getServiceDescription(serviceValue);
  }

  public isServiceSelected(serviceValue: string): boolean {
    return this.selectedService() === serviceValue;
  }

  public onTotalMachinesChange(value: number | null): void {
    this.formService.onTotalMachinesChange(this.medrForm, value);
    this.isFormValid.set(this.medrForm.valid);
    this.validityUpdate.emit(this.medrForm.valid);
  }

  // Debug method - call this from browser console to check form state
  public debugFormState(): void {
    this.formService.debugFormState(this.medrForm);
  }
}
